// Some browsers hand the page a Storage object that is null or whose getter
// throws: Chrome with site data blocked ("Access is denied for this
// document"), older Safari private windows (setItem throws), and a few in-app
// browsers. The portal touches localStorage/sessionStorage in 200+ places,
// most of them during boot, so those members crashed on the first read
// (Clarity: 77 sessions in 30 days). When the real storage is unusable we
// shadow it with an in-memory stand-in: nothing persists between page loads,
// but the page works.

export const createMemoryStorage = () => {
  const store = new Map();
  return {
    get length() {
      return store.size;
    },
    key(index) {
      const keys = Array.from(store.keys());
      return index >= 0 && index < keys.length ? keys[index] : null;
    },
    getItem(key) {
      const k = String(key);
      return store.has(k) ? store.get(k) : null;
    },
    setItem(key, value) {
      store.set(String(key), String(value));
    },
    removeItem(key) {
      store.delete(String(key));
    },
    clear() {
      store.clear();
    },
  };
};

const PROBE_KEY = '__wg_storage_probe__';

export const isStorageUsable = (storage) => {
  try {
    if (!storage) return false;
    storage.setItem(PROBE_KEY, '1');
    storage.removeItem(PROBE_KEY);
    return true;
  } catch (e) {
    return false;
  }
};

/**
 * Make `target[name]` ('localStorage' | 'sessionStorage') safe to use.
 * Returns the storage the app will see from now on.
 */
export const ensureStorage = (name, target = window) => {
  let current = null;
  try {
    current = target[name];
  } catch (e) {
    current = null; // accessor threw (SecurityError)
  }
  if (isStorageUsable(current)) return current;

  const fallback = createMemoryStorage();
  try {
    Object.defineProperty(target, name, { value: fallback, configurable: true, writable: true });
  } catch (e) {
    // Could not shadow the broken accessor; callers keep the original.
  }
  return fallback;
};

export const installStorageShims = (target = window) => {
  if (typeof target === 'undefined') return;
  ensureStorage('localStorage', target);
  ensureStorage('sessionStorage', target);
};
